import { forwardRef, useImperativeHandle } from "react";
import { ModifyResidentRef } from "../interfaces";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { maxDateValidation, requiredValidation } from "ui/utils/Validators";
import { useHandleResidentVaccinations } from "../../hooks/useHandleResidentVaccinations";
import { Text, View } from "react-native";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import { AddIcon, TrashIcon } from "ui/gluestack/icon";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";

const ResidentVaccinationsFormTemplate = forwardRef<ModifyResidentRef, {}>(
  (_, ref) => {
    const { control, submitForm, fields, append, remove } =
      useHandleResidentVaccinations();

    useImperativeHandle(
      ref,
      () => ({
        continue: () => submitForm(),
      }),
      [submitForm],
    );
    return (
      <View>
        {fields.map((field, index) => (
          <View
            key={field.id}
            className="flex flex-row mb-8 items-start md:w-9/12 gap-x-2"
          >
            <Text>{index + 1}.</Text>
            <View className="flex-col md:flex-row flex-1  gap-x-8 gap-y-4">
              <View className="md:max-w-72 w-full">
                <FormControlledInput
                  className="flex-1"
                  id={`vaccination-${index}`}
                  name={`vaccines.${index}.name`}
                  control={control}
                  label={"Vaccination"}
                  placeholder={"Enter vaccination"}
                  isRequired
                  rules={{
                    required:
                      index !== 0
                        ? requiredValidation("Vaccination")
                        : undefined,
                  }}
                />
              </View>
              <View className="md:w-72 w-full">
                <FormControlledDatePicker
                  id={`date_of_administration-${index}`}
                  name={`vaccines.${index}.date_of_administration`}
                  control={control}
                  label={"Date of administration"}
                  placeholder={"00/00/0000"}
                  isRequired
                  rules={{
                    required: undefined,
                    validate: {
                      maxDate: maxDateValidation(
                        new Date(),
                        "Date of vaccination must be before today",
                      ),
                      requiredIf: (value, formValues) => {
                        if (formValues.vaccines[index]?.name && !value) {
                          return "Date of administration is required";
                        }
                        return true;
                      },
                    },
                  }}
                />
                {index > 0 ? (
                  <View className="self-end mt-3">
                    <Button
                      size="xs"
                      action="negative"
                      variant="outline"
                      onPress={() => remove(index)}
                    >
                      <ButtonText>Remove record</ButtonText>
                      <ButtonIcon as={TrashIcon} />
                    </Button>
                  </View>
                ) : null}
              </View>
            </View>
          </View>
        ))}
        <Button
          action="secondary"
          className="bg-secondary-0 w-28 px-3 mt-3 mb-5"
          onPress={append}
        >
          <Text>Add new</Text>
          <ButtonIcon as={AddIcon}></ButtonIcon>
        </Button>
      </View>
    );
  },
);
ResidentVaccinationsFormTemplate.displayName =
  "ResidentVaccinationsFormTemplate";
export { ResidentVaccinationsFormTemplate };
